import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BUSINESS } from "../data";

const SECTIONS = [
  {
    h: "Data We Collect",
    items: [
      "Contact details you submit: name, email address, and phone number.",
      "Property interest details: buy/sell intent, budget or asking price, timeline, and any message you provide.",
      "Basic technical data such as browser type and pages visited, collected automatically to keep the site secure and functional.",
    ],
  },
  {
    h: "Why We Collect It",
    items: [
      "To respond to your inquiry and provide land buying or selling assistance.",
      "To prepare and present cash offers or matching vacant lot opportunities.",
      "To improve our services and communicate relevant updates you have requested.",
    ],
  },
  {
    h: "Data Retention",
    items: [
      "We keep lead information only as long as needed to serve your request and meet legal or business record requirements.",
      "You may request deletion of your information at any time (see User Rights below).",
    ],
  },
  {
    h: "Third-Party Use",
    items: [
      "We do not sell your personal information.",
      "We may share limited details with trusted service providers (e.g., email delivery) solely to operate our business.",
      "Information may be disclosed if required by law or to protect our legal rights.",
    ],
  },
  {
    h: "Data Security",
    items: [
      "Submissions are transmitted over encrypted connections and stored on access-controlled systems.",
      "We apply reasonable administrative and technical safeguards, though no method of transmission is 100% secure.",
    ],
  },
  {
    h: "Your Rights",
    items: [
      "Request access to the personal information we hold about you.",
      "Request correction or deletion of your information.",
      "Opt out of non-essential communications at any time by contacting us.",
    ],
  },
];

export default function Privacy() {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <main className="bg-sand min-h-screen pt-32 pb-24 px-5 md:px-10" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 text-terracotta hover:gap-3 transition-all duration-300 mb-10 text-sm tracking-wide">
          <ArrowLeft size={17} /> Back to Home
        </Link>
        <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">Legal</p>
        <h1 className="font-display text-5xl md:text-6xl text-forest leading-none mb-4">Privacy Policy</h1>
        <p className="text-forest/50 text-sm mb-14">Last updated: September 2025.</p>

        <p className="text-forest/75 leading-relaxed mb-12">
          {BUSINESS.legal} ("we", "us") respects your privacy. This policy explains what we collect through this
          website and our contact forms, why we collect it, and the choices you have.
        </p>

        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`privacy-section-${s.h.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="font-display text-3xl text-forest mb-5">{s.h}</h2>
              <ul className="space-y-3">
                {s.items.map((it, i) => (
                  <li key={i} className="flex gap-3 text-forest/75 leading-relaxed">
                    <span className="mt-2.5 h-1.5 w-1.5 bg-terracotta shrink-0 rounded-full" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-14 border-t border-forest/15 pt-8">
          <h2 className="font-display text-3xl text-forest mb-3">Contact Us</h2>
          <p className="text-forest/75 leading-relaxed">
            Questions about this policy or your data? Reach us at{" "}
            <a href={`mailto:${BUSINESS.email}`} className="text-terracotta">{BUSINESS.email}</a> or{" "}
            <a href={BUSINESS.phoneHref} className="text-terracotta">{BUSINESS.phone}</a>.
          </p>
          <p className="text-forest/40 text-sm mt-10">© 2025 {BUSINESS.legal}</p>
        </div>
      </div>
    </main>
  );
}
